import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const TeacherQuizCreation = () => {
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState(""); // duration in seconds
  const [questions, setQuestions] = useState([]); // Array of question objects
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  // Add a new blank question object
  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      { question: "", options: "", correctAnswer: "", marks: "" },
    ]);
  };

  // Fetch teacher subjects
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!token) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSubjects(res.data.subjects || []);
      } catch (err) {
        console.error("Error fetching subjects:", err);
        setSnackbar({
          open: true,
          message: "Failed to load subjects",
          severity: "error",
        });
      }
    };
    fetchSubjects();
  }, [token]);

  // Update a question field at a given index
  const updateQuestionField = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index][field] = value;
    setQuestions(updatedQuestions);
  };

  // Remove a question at a given index
  const removeQuestion = (index) => {
    const updatedQuestions = [...questions];
    updatedQuestions.splice(index, 1);
    setQuestions(updatedQuestions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !duration || !subject) {
      setSnackbar({ open: true, message: "Title, subject and duration are required.", severity: "warning" });
      return;
    }
    if (questions.length === 0) {
      setSnackbar({ open: true, message: "Please add at least one question.", severity: "warning" });
      return;
    }
    // Validate each question and format options as an array
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question || !q.options || !q.correctAnswer || !q.marks) {
        setSnackbar({ open: true, message: `Please fill all fields for question ${i + 1}.`, severity: "warning" });
        return;
      }
    }
    const formattedQuestions = questions.map((q) => ({
      question: q.question,
      options: q.options
        .split(",")
        .map((option) => option.trim())
        .filter((option) => option),
      correctAnswer: q.correctAnswer,
      marks: Number(q.marks),
    }));

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/quiz/create`,
        { title, duration: Number(duration), subject, questions: formattedQuestions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: response.data.message, severity: 'success' });

      setTitle("");
      setDuration("");
      setQuestions([]);
      setSubject("");
      setTimeout(() => navigate("/teacher-dashboard"), 1500); // Redirect after success
    } catch (error) {
      console.error(
        "Error creating quiz:",
        error.response ? error.response.data : error.message
      );
      setSnackbar({ open: true, message: "Error creating quiz. Please try again.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className="quiz-creation-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .quiz-creation-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .quiz-creation-container::before {
          content: '';
          position: absolute;
          top: 15%;
          right: 10%;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: float 4s ease-in-out infinite;
        }

        .quiz-creation-container::after {
          content: '';
          position: absolute;
          bottom: 20%;
          left: 12%;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 3.5s ease-in-out infinite;
          animation-delay: 1.8s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(8deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes slideInFromBottom {
          from { 
            opacity: 0; 
            transform: translateY(100px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        .pulse-element {
          position: absolute;
          top: 35%;
          right: 30%;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.06);
          animation: pulse 3s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }

        .quiz-wrapper {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2.2rem, 4vw, 3rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .form-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 3rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: slideIn 0.6s ease-out 0.4s both;
          position: relative;
          overflow: hidden;
        }

        .form-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
          border-radius: 24px 24px 0 0;
        }

        .form-title {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .form-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
        }

        .form-title-text {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
        }

        .form-group {
          margin-bottom: 2rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.95rem;
        }

        .form-input {
          width: 100%;
          padding: 1.25rem;
          border: 2px solid rgba(209, 213, 219, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          box-sizing: border-box;
          resize: vertical;
        }

        .form-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .form-select {
          width: 100%;
          padding: 1.25rem;
          border: 2px solid rgba(209, 213, 219, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          cursor: pointer;
        }

        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .questions-section {
          margin: 3rem 0;
        }

        .questions-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 2rem;
        }

        .questions-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
        }

        .questions-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(45deg, #10b981 30%, #059669 90%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .add-question-btn {
          background: linear-gradient(45deg, #10b981 30%, #059669 90%);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
        }

        .add-question-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
        }

        .question-card {
          background: rgba(255, 255, 255, 0.8);
          border: 2px solid rgba(209, 213, 219, 0.2);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          transition: all 0.3s ease;
          position: relative;
          backdrop-filter: blur(10px);
        }

        .question-card:hover {
          border-color: rgba(102, 126, 234, 0.3);
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
        }

        .question-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }

        .question-number {
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .remove-question-btn {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border: 1px solid rgba(239, 68, 68, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
        }

        .remove-question-btn:hover {
          background: rgba(239, 68, 68, 0.15);
          transform: translateY(-1px);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .submit-btn {
          width: 100%;
          padding: 1.5rem 2rem;
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          box-shadow: 0 6px 25px rgba(254, 107, 139, 0.4);
          margin-top: 2rem;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 12px 35px rgba(254, 107, 139, 0.5);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .snackbar {
          position: fixed;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          padding: 1rem 2rem;
          border-radius: 12px;
          color: white;
          font-weight: 500;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          backdrop-filter: blur(10px);
          z-index: 1000;
          animation: slideInFromBottom 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          max-width: 400px;
        }

        .snackbar.success {
          background: rgba(16, 185, 129, 0.9);
        }

        .snackbar.error {
          background: rgba(239, 68, 68, 0.9);
        }

        .snackbar.warning {
          background: rgba(245, 158, 11, 0.9);
        }

        .snackbar.info {
          background: rgba(59, 130, 246, 0.9);
        }

        .snackbar-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          opacity: 0.8;
        }

        .snackbar-close:hover {
          opacity: 1;
          background: rgba(255, 255, 255, 0.1);
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .quiz-creation-container {
            padding: 1rem 0.5rem;
          }
          
          .form-container {
            padding: 2rem 1.5rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .questions-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .question-header {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .snackbar {
            left: 1rem;
            right: 1rem;
            transform: none;
            max-width: none;
          }
        }
      `}</style>

      <div className="pulse-element"></div>
      
      <div className="quiz-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9,5V9H21V7.5L16,3L21,7.5V5H9M21,11H9V21H21V11M17,12V18H11V12H17Z"/>
            </svg>
            Teacher Dashboard
          </div>
          
          <h1 className="main-title">
            <span className="gradient-text">Create</span> Weekly Quiz
          </h1>
          
          <p className="subtitle">
            Design engaging quizzes for your students
          </p>
        </div>

        {/* Form Container */}
        <div className="form-container">
          <div className="form-title">
            <div className="form-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9,5V9H21V7.5L16,3L21,7.5V5H9M21,11H9V21H21V11M17,12V18H11V12H17Z"/>
              </svg>
            </div>
            <div className="form-title-text">Quiz Configuration</div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Quiz Title *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Mathematics Weekly Quiz - Chapter 5"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Duration (seconds) *</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="e.g. 1800 (30 minutes)"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Subject *</label>
                <select
                  className="form-select"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                >
                  <option value="">-- Select Subject --</option>
                  {subjects.map((subj, idx) => (
                    <option key={idx} value={subj}>{subj}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Questions Section */}
            <div className="questions-section">
              <div className="questions-header">
                <div className="questions-title">
                  <div className="questions-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z"/>
                    </svg>
                  </div>
                  Questions ({questions.length})
                </div>
                
                <button
                  type="button"
                  className="add-question-btn"
                  onClick={addQuestion}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                  </svg>
                  Add Question
                </button>
              </div>

              {questions.map((q, index) => (
                <div key={index} className="question-card">
                  <div className="question-header">
                    <div className="question-number">
                      Question {index + 1}
                    </div>
                    <button
                      type="button"
                      className="remove-question-btn"
                      onClick={() => removeQuestion(index)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                      </svg>
                      Remove
                    </button>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Question Text *</label>
                    <textarea
                      className="form-input"
                      placeholder="Enter your question here..."
                      rows="3"
                      value={q.question}
                      onChange={(e) =>
                        updateQuestionField(index, "question", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Options (comma separated) *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Option A, Option B, Option C, Option D"
                      value={q.options}
                      onChange={(e) =>
                        updateQuestionField(index, "options", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Correct Answer *</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter the correct answer"
                        value={q.correctAnswer}
                        onChange={(e) =>
                          updateQuestionField(index, "correctAnswer", e.target.value)
                        }
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Marks *</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g. 5"
                        value={q.marks}
                        onChange={(e) =>
                          updateQuestionField(index, "marks", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}

              {questions.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem', 
                  color: '#6b7280',
                  background: 'rgba(243, 244, 246, 0.5)',
                  borderRadius: '12px',
                  border: '2px dashed rgba(209, 213, 219, 0.5)'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" style={{ marginBottom: '1rem', opacity: 0.5 }}>
                    <path d="M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z"/>
                  </svg>
                  <div style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    No questions added yet
                  </div>
                  <div style={{ fontSize: '0.9rem' }}>
                    Click "Add Question" to start building your quiz
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Creating Quiz...
                </>
              ) : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                  </svg>
                  Create Quiz
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Snackbar Notification */}
      {snackbar.open && (
        <div className={`snackbar ${snackbar.severity}`}>
          <span>{snackbar.message}</span>
          <button className="snackbar-close" onClick={closeSnackbar}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default TeacherQuizCreation;